import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import AnaliseTemposOF from './AnaliseTemposOF';
import EditarRegisto from './gestor/EditarRegisto';
import CriarRegistoManual from './gestor/CriarRegistoManual';
import { toast } from 'sonner';
import { formatTimeLisbon, formatOnlyDateLisbon } from '../lib/utils';
import { buildExportRows as buildExportRowsExt, buildReportHtml as buildReportHtmlExt, exportAcompanhamentoPdf, downloadAcompanhamentoHtml } from './acompanhamentoExport';
import { 
  Package, ArrowLeft, Activity, Filter, Layers, AlertCircle, 
  ChevronDown, ChevronRight, Users, CheckCircle2, Clock, Calendar, PieChart, Edit3, Edit, RefreshCw,
  Plus, CheckSquare, Snowflake, Download, FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcompanhamentoFullscreen({ ofId, numeroOf, marca, dataEntrega, loteIdPreSelecionado, onClose }) {
  const [loading, setLoading] = useState(true);
  const [acompanharData, setAcompanharData] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [filtroLote, setFiltroLote] = useState('todos');
  const [categoriasExpandidas, setCategoriasExpandidas] = useState({});
  const [showAnaliseTempos, setShowAnaliseTempos] = useState(false);
  const [ofCompleta, setOfCompleta] = useState(null);
  const [operacoesBase, setOperacoesBase] = useState([]);
  
  // Estados para edição de registos
  const [showRegistosDialog, setShowRegistosDialog] = useState(false);
  const [selectedOperacao, setSelectedOperacao] = useState(null);
  const [registosOperacao, setRegistosOperacao] = useState([]);
  const [showEditarRegisto, setShowEditarRegisto] = useState(false);
  const [selectedRegistoId, setSelectedRegistoId] = useState(null);
  const [operadores, setOperadores] = useState([]);
  
  // Estados para criar registo manual e concluir
  const [showCriarRegisto, setShowCriarRegisto] = useState(false);
  const [criarRegistoOp, setCriarRegistoOp] = useState(null);
  const [criarRegistoLote, setCriarRegistoLote] = useState(null);
  const [criarRegistoModo, setCriarRegistoModo] = useState('registo'); // 'registo' | 'concluir'
  
  // Estado para auto-refresh
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Exportar CSV
  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/ofs/${ofId}/acompanhamento/export?format=csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `acompanhamento_${numeroOf || ofId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exportado com sucesso');
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
      toast.error('Erro ao exportar CSV');
    }
  };
  
  // Helpers de formatação para export
  
  // Wrappers para módulo de export (acompanhamentoExport.js)
  const buildExportRows = () => buildExportRowsExt(acompanharData, lotesFiltrados);
  const buildReportHtml = () => buildReportHtmlExt({ acompanharData, lotesFiltrados, numeroOf, marca, dataEntrega });
  
  // Exportar como ficheiro HTML (fallback)
  const handleExportHTML = () => {
    downloadAcompanhamentoHtml({ acompanharData, lotesFiltrados, numeroOf, marca, dataEntrega });
    toast.success('HTML exportado com sucesso');
  };
  
  // Exportar como PDF (com fallback para HTML em caso de erro)
  const handleExportPDF = async () => {
    toast.info('A gerar PDF...');
    const result = exportAcompanhamentoPdf({
      acompanharData, lotesFiltrados, numeroOf, marca, dataEntrega,
      onFallback: () => handleExportHTML(),
    });
    if (result.ok) toast.success('PDF exportado com sucesso');
    else toast.error('Erro ao gerar PDF. Descarregado como HTML.');
  };

  // Auto-refresh a cada 30 segundos
  useEffect(() => {
    if (ofId) {
      loadData();
      
      const refreshInterval = setInterval(() => {
        loadDataSilent();
      }, 30000); // 30 segundos
      
      return () => clearInterval(refreshInterval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ofId]);

  // Carregamento silencioso (sem loading spinner)
  const loadDataSilent = async () => {
    try {
      const [acompRes, ofRes] = await Promise.all([
        axios.get(`${API}/ofs/${ofId}/acompanhamento`),
        axios.get(`${API}/ofs/${ofId}`)
      ]);
      setAcompanharData(acompRes.data);
      setOfCompleta(ofRes.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Erro no auto-refresh:', error);
    }
  };

  // Refresh manual com indicador visual
  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadDataSilent();
      toast.success('Dados atualizados');
    } catch (error) {
      toast.error('Erro ao atualizar');
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [acompRes, catRes, ofRes, operacoesRes, operadoresRes] = await Promise.all([
        axios.get(`${API}/ofs/${ofId}/acompanhamento`),
        axios.get(`${API}/categorias`),
        axios.get(`${API}/ofs/${ofId}`),
        axios.get(`${API}/operacoes`),
        axios.get(`${API}/operadores`)
      ]);
      setAcompanharData(acompRes.data);
      setCategorias(catRes.data.sort((a, b) => (a.ordem || 0) - (b.ordem || 0)));
      setOfCompleta(ofRes.data);
      setLastRefresh(new Date());
      setOperadores(operadoresRes.data || []);
      
      // Normalizar operações base
      const opsNormalizadas = (operacoesRes.data || []).map(op => ({
        ...op,
        categoria: op.categoria_nome || op.categoria,
        tempo_standard: op.tempo_padrao_segundos || op.tempo_standard || 0
      }));
      setOperacoesBase(opsNormalizadas);
      
      // Expandir todas as categorias por defeito
      const expanded = {};
      catRes.data.forEach(cat => {
        expanded[cat.id] = true;
      });
      setCategoriasExpandidas(expanded);
      
      // Se tiver lote pré-selecionado, filtrar para mostrar apenas esse lote
      if (loteIdPreSelecionado && acompRes.data?.lotes) {
        const loteEncontrado = acompRes.data.lotes.find(l => l.lote_id === loteIdPreSelecionado);
        if (loteEncontrado) {
          setFiltroLote(loteIdPreSelecionado);
          // Se o lote tiver categoria, também filtrar por categoria
          if (loteEncontrado.categoria_id) {
            setFiltroCategoria(loteEncontrado.categoria_id);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função para carregar registos de uma operação
  const loadRegistosOperacao = async (operacaoId) => {
    try {
      const res = await axios.get(`${API}/ofs/${ofId}/registos`);
      const registosFiltrados = res.data.filter(r => r.operacao_id === operacaoId);
      setRegistosOperacao(registosFiltrados);
    } catch (error) {
      console.error('Erro ao carregar registos:', error);
      toast.error('Erro ao carregar registos');
    }
  };

  // Efeito para carregar registos quando abre o diálogo
  useEffect(() => {
    if (showRegistosDialog && selectedOperacao) {
      loadRegistosOperacao(selectedOperacao.operacao_id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRegistosDialog, selectedOperacao]);

  // Organizar lotes por categoria
  const lotesPorCategoria = useMemo(() => {
    if (!acompanharData?.lotes) return {};
    
    const result = {};
    acompanharData.lotes.forEach(lote => {
      const catId = lote.categoria_id || 'sem_categoria';
      if (!result[catId]) {
        result[catId] = [];
      }
      result[catId].push(lote);
    });
    return result;
  }, [acompanharData]);

  // Filtrar lotes baseado nos filtros seleccionados
  const lotesFiltrados = useMemo(() => {
    if (!acompanharData?.lotes) return [];
    
    let lotes = [...acompanharData.lotes];
    
    if (filtroCategoria !== 'todas') {
      lotes = lotes.filter(l => l.categoria_id === filtroCategoria);
    }
    
    if (filtroLote !== 'todos') {
      lotes = lotes.filter(l => l.id === filtroLote);
    }
    
    return lotes;
  }, [acompanharData, filtroCategoria, filtroLote]);

  // Função para formatar tempo de segundos para "Xh Xmin" ou "Xmin Xs"
  const formatarTempo = (segundos) => {
    if (!segundos || segundos === 0) return '0min';
    
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const segs = Math.floor(segundos % 60);
    
    if (horas > 0) {
      return minutos > 0 ? `${horas}h ${minutos}min` : `${horas}h`;
    } else if (minutos > 0) {
      return segs > 0 ? `${minutos}min ${segs}s` : `${minutos}min`;
    } else {
      return `${segs}s`;
    }
  };

  // Operações do lote selecionado com dados específicos do lote
  // IMPORTANTE: Filtra apenas operações da categoria do lote
  const operacoesDoLote = useMemo(() => {
    if (filtroLote === 'todos' || !acompanharData?.operacoes) return [];
    
    const lote = acompanharData.lotes?.find(l => l.id === filtroLote);
    if (!lote) return [];
    
    // Lote ARR (Arranjo): exibe a "Tarefa de Arranjo" virtual (descrição) + operações permitidas
    const isArrLote = lote.categoria_codigo === 'ARR' || lote.origem === 'arranjo';
    if (isArrLote) {
      const permitidas = Array.isArray(lote.operacoes_permitidas) ? lote.operacoes_permitidas : [];
      // Tarefa de Arranjo virtual (sempre presente para ARR)
      const opsEstado = Array.isArray(lote.operacoes_estado) ? lote.operacoes_estado : [];
      const opsResumo = Array.isArray(lote.resumo_operacoes) ? lote.resumo_operacoes : [];
      const totalProduzida = opsEstado.reduce((acc, oe) => acc + (oe.quantidade_produzida || 0), 0);
      const totalDefeitos = opsEstado.reduce((acc, oe) => acc + (oe.defeitos || 0), 0);
      const totalArranjos = opsEstado.reduce((acc, oe) => acc + (oe.arranjos || 0), 0);
      const tempoTotal = opsResumo.reduce((acc, ro) => acc + (ro.tempo_segundos || 0), 0);
      const operadorAtivo = opsResumo.find(ro => ro.operador_ativo_id)?.operador_ativo_id || null;
      const estadoProducao = opsResumo.find(ro => ro.estado_producao)?.estado_producao || null;
      const qtdLote = lote.quantidade_ajustada || lote.quantidade || 0;
      const tarefaArranjo = {
        operacao_id: `arr-virtual-${lote.id}`,
        codigo: 'ARR',
        nome: lote.descricao || lote.observacoes || 'Trabalho de arranjo',
        ordem: 0,
        categoria_id: lote.categoria_id,
        is_contagem: false,
        quantidade_produzida_lote: totalProduzida,
        defeitos_lote: totalDefeitos,
        arranjos_lote: totalArranjos,
        percentagem_lote: qtdLote > 0 ? Math.round((totalProduzida / qtdLote) * 100) : 0,
        estado_producao_lote: estadoProducao,
        operador_ativo_id: operadorAtivo,
        tempo_segundos_lote: tempoTotal,
      };
      // Operações permitidas (ou todas as ops da OF se nenhuma definida)
      const opsBase = permitidas.length > 0
        ? acompanharData.operacoes.filter(op => permitidas.includes(op.operacao_id || op.id))
        : acompanharData.operacoes;
      return [tarefaArranjo, ...opsBase];
    }
    
    // Verificar se a categoria do lote é especial
    const categoriaLote = categorias.find(c => c.id === lote.categoria_id);
    const isCategoriaEspecial = categoriaLote?.especial === true;
    
    // Filtrar operações apenas da categoria do lote (exceto se for categoria especial)
    const operacoesFiltradas = acompanharData.operacoes.filter(op => {
      // Se a categoria do lote é especial, mostrar todas as operações
      if (isCategoriaEspecial) return true;
      // Senão, mostrar apenas operações da mesma categoria
      return op.categoria_id === lote.categoria_id;
    });
    
    // Mapear operações com dados específicos do lote
    return operacoesFiltradas.map(op => {
      // Procurar estado da operação no lote
      const opEstado = lote.operacoes_estado?.find(oe => oe.operacao_id === op.operacao_id);
      
      // Procurar estado em tempo real da operação no resumo_operacoes do lote
      const opResumo = Array.isArray(lote.resumo_operacoes) 
        ? lote.resumo_operacoes.find(ro => ro.operacao_id === op.operacao_id)
        : null;
      
      return {
        ...op,
        // Dados específicos do lote
        quantidade_produzida_lote: opEstado?.quantidade_produzida || 0,
        defeitos_lote: opEstado?.defeitos || 0,
        arranjos_lote: opEstado?.arranjos || 0,
        percentagem_lote: lote.quantidade > 0 
          ? Math.round(((opEstado?.quantidade_produzida || 0) / lote.quantidade) * 100)
          : 0,
        estado_producao_lote: opResumo?.estado_producao || null,
        operador_ativo_id: opResumo?.operador_ativo_id || null,
        tempo_segundos_lote: opResumo?.tempo_segundos || 0,
      };
    });
  }, [acompanharData, filtroLote, categorias]);

  // Operações filtradas (para quando nenhum lote está selecionado)
  const operacoesFiltradas = useMemo(() => {
    if (!acompanharData?.operacoes) return [];
    
    // Se um lote específico está seleccionado, usar operacoesDoLote
    if (filtroLote !== 'todos') {
      return operacoesDoLote;
    }
    
    // Se uma categoria específica está seleccionada
    if (filtroCategoria !== 'todas') {
      return acompanharData.operacoes.filter(op => op.categoria_id === filtroCategoria);
    }
    
    return acompanharData.operacoes;
  }, [acompanharData, filtroCategoria, filtroLote, operacoesDoLote]);

  const toggleCategoria = (catId) => {
    setCategoriasExpandidas(prev => ({
      ...prev,
      [catId]: !prev[catId]
    }));
  };

  const getCategoriaInfo = (catId) => {
    return categorias.find(c => c.id === catId) || { nome: 'Sem Categoria', codigo: '?' };
  };

  if (loading) {
    return (
      <div className="fixed inset-y-0 left-64 right-0 bg-stone-100 z-40 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-y-0 left-64 right-0 bg-stone-100 z-40 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-navy via-navy/90 to-navy/80 text-white p-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="h-6 w-6" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-3">
                <Activity className="h-7 w-7" />
                Acompanhamento - {numeroOf}
              </h1>
              <div className="flex items-center gap-4 text-white/70 mt-1 text-sm">
                {marca && <span>Marca: <span className="text-white font-medium">{marca}</span></span>}
                {dataEntrega && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Entrega: <span className="text-white font-medium">{new Date(dataEntrega).toLocaleDateString('pt-PT')}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Resumo no Header */}
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-2xl font-bold">{acompanharData?.of?.quantidade_total || 0}</p>
              <p className="text-xs text-white/70">Total</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-300">{acompanharData?.resumo?.total_anomalias || 0}</p>
              <p className="text-xs text-white/70">Anomalias</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-300">{acompanharData?.resumo?.percentagem_concluida || 0}%</p>
              <p className="text-xs text-white/70">Concluído</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{acompanharData?.lotes?.length || 0}</p>
              <p className="text-xs text-white/70">Lotes</p>
            </div>
            
            {/* Botão Análise de Tempos */}
            <Button
              onClick={() => setShowAnaliseTempos(true)}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30 gap-2"
              variant="outline"
            >
              <PieChart className="h-4 w-4" />
              Análise
            </Button>
            
            {/* Botões de Exportação */}
            <Button
              onClick={handleExportCSV}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30 gap-2"
              variant="outline"
              data-testid="btn-export-csv"
            >
              <Download className="h-4 w-4" />
              CSV
            </Button>
            <Button
              onClick={handleExportPDF}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30 gap-2"
              variant="outline"
              data-testid="btn-export-pdf"
            >
              <FileText className="h-4 w-4" />
              PDF
            </Button>
            <Button
              onClick={handleExportHTML}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30 gap-2"
              variant="outline"
              size="sm"
              data-testid="btn-export-html"
            >
              <Download className="h-4 w-4" />
              HTML
            </Button>
            
            {/* Botão Refresh Manual */}
            <Button
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30 gap-2"
              variant="outline"
              title={`Última atualização: ${lastRefresh.toLocaleTimeString('pt-PT')}`}
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'A atualizar...' : 'Atualizar'}
            </Button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white border-b p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-stone-500" />
            <span className="text-sm font-medium text-stone-700">Filtrar por:</span>
          </div>
          
          {/* Filtro por Categoria */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-stone-500">Categoria:</span>
            <div className="flex gap-1 flex-wrap">
              <Button 
                size="sm" 
                variant={filtroCategoria === 'todas' ? 'default' : 'outline'}
                onClick={() => { setFiltroCategoria('todas'); setFiltroLote('todos'); }}
              >
                Todas
              </Button>
              {categorias.map((cat) => {
                const lotesNaCategoria = lotesPorCategoria[cat.id] || [];
                if (lotesNaCategoria.length === 0) return null;
                return (
                  <Button 
                    key={cat.id}
                    size="sm" 
                    variant={filtroCategoria === cat.id ? 'default' : 'outline'}
                    onClick={() => { setFiltroCategoria(cat.id); setFiltroLote('todos'); }}
                    className="gap-1"
                  >
                    <span className="font-mono text-xs">{cat.codigo}</span>
                    {cat.nome}
                    <span className="text-xs opacity-70">({lotesNaCategoria.length})</span>
                  </Button>
                );
              })}
            </div>
          </div>
          
          {/* Filtro por Lote */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-stone-500">Lote:</span>
            <div className="flex gap-1 flex-wrap">
              <Button 
                size="sm" 
                variant={filtroLote === 'todos' ? 'default' : 'outline'}
                onClick={() => setFiltroLote('todos')}
              >
                Todos os Lotes
              </Button>
              {(filtroCategoria !== 'todas' ? lotesPorCategoria[filtroCategoria] || [] : acompanharData?.lotes || []).map((lote) => (
                <Button 
                  key={lote.id}
                  size="sm" 
                  variant={filtroLote === lote.id ? 'default' : 'outline'}
                  onClick={() => setFiltroLote(lote.id)}
                  className={`gap-1 ${lote.status === 'aprovado' ? 'border-green-300' : 'border-amber-300'}`}
                >
                  <Package className="h-3 w-3" />
                  {lote.codigo_lote || `Lote #${lote.numero_lote}`}
                  <span className="text-xs opacity-70">({lote.quantidade} un.)</span>
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Coluna Esquerda: Lotes por Categoria */}
          <div className="lg:col-span-1 space-y-4">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              Lotes por Categoria
            </h2>
            
            {categorias.map((categoria) => {
              const lotesCategoria = lotesPorCategoria[categoria.id] || [];
              if (lotesCategoria.length === 0 && filtroCategoria === 'todas') return null;
              if (filtroCategoria !== 'todas' && filtroCategoria !== categoria.id) return null;
              
              const isExpanded = categoriasExpandidas[categoria.id];
              const totalProduzido = lotesCategoria.reduce((acc, l) => acc + (l.quantidade_produzida || 0), 0);
              const totalQuantidade = lotesCategoria.reduce((acc, l) => acc + (l.quantidade || 0), 0);
              const percentagemCategoria = totalQuantidade > 0 ? Math.round((totalProduzido / totalQuantidade) * 100) : 0;
              
              return (
                <Card key={categoria.id} className="overflow-hidden">
                  {/* Header da Categoria */}
                  <div 
                    className="p-3 bg-stone-50 border-b cursor-pointer flex items-center justify-between hover:bg-stone-100 transition-colors"
                    onClick={() => toggleCategoria(categoria.id)}
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      <span className="font-mono text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">{categoria.codigo}</span>
                      <span className="font-semibold">{categoria.nome}</span>
                      <span className="text-xs text-stone-500">({lotesCategoria.length} lotes)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-stone-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${percentagemCategoria >= 100 ? 'bg-green-500' : 'bg-primary'}`}
                          style={{ width: `${percentagemCategoria}%` }}
                        />
                      </div>
                      <span className="text-sm font-bold text-primary">{percentagemCategoria}%</span>
                    </div>
                  </div>
                  
                  {/* Lista de Lotes */}
                  {isExpanded && (
                    <div className="divide-y">
                      {lotesCategoria.map((lote) => {
                        const isSelected = filtroLote === lote.id;
                        return (
                          <div 
                            key={lote.id}
                            className={`p-3 cursor-pointer transition-colors ${isSelected ? 'bg-primary/10 border-l-4 border-l-primary' : 'hover:bg-stone-50'}`}
                            onClick={() => setFiltroLote(isSelected ? 'todos' : lote.id)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Package className="h-4 w-4 text-stone-400" />
                                <span className="font-semibold">{lote.codigo_lote || `Lote #${lote.numero_lote}`}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${lote.status === 'aprovado' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {lote.status === 'aprovado' ? 'Aprovado' : 'Pendente'}
                                </span>
                              </div>
                              <span className="text-lg font-bold text-primary">{lote.percentagem || 0}%</span>
                            </div>
                            <div className="w-full h-2 bg-stone-200 rounded-full overflow-hidden mb-2">
                              <div 
                                className={`h-full rounded-full ${(lote.percentagem || 0) >= 100 ? 'bg-green-500' : 'bg-primary'}`}
                                style={{ width: `${Math.min(lote.percentagem || 0, 100)}%` }}
                              />
                            </div>
                            <div className="flex items-center justify-between text-xs text-stone-500">
                              <span>{lote.quantidade_produzida || 0} / {lote.quantidade} un.</span>
                              <div className="flex items-center gap-3">
                                {(lote.defeitos || 0) > 0 && (
                                  <span className="text-red-500 flex items-center gap-1">
                                    <AlertCircle className="h-3 w-3" /> {lote.defeitos}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <Users className="h-3 w-3" /> {lote.participantes?.length || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
          
          {/* Coluna Direita: Detalhes e Operações */}
          <div className="lg:col-span-2 space-y-4">
            {/* Resumo do Lote Selecionado */}
            {filtroLote !== 'todos' && (() => {
              const lote = acompanharData?.lotes?.find(l => l.id === filtroLote);
              if (!lote) return null;
              const isArrLote = lote.categoria_codigo === 'ARR' || lote.origem === 'arranjo';
              // Para ARR, mostrar 1 op virtual em vez de N ops da OF
              const opsConcluidas = isArrLote
                ? ((lote.resumo_operacoes?.concluidas || 0) > 0 ? 1 : 0)
                : (lote.resumo_operacoes?.concluidas || 0);
              const opsTotal = isArrLote ? 1 : (lote.resumo_operacoes?.total || 0);
              
              return (
                <Card className="p-4 bg-gradient-to-r from-primary/5 to-primary/10 border-2 border-primary/20">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-bold flex items-center gap-2">
                        <Package className="h-5 w-5 text-primary" />
                        {lote.codigo_lote || `Lote #${lote.numero_lote}`}
                      </h3>
                      <p className="text-stone-600 text-sm">
                        Categoria: <span className="font-semibold">{lote.categoria_nome || 'N/A'}</span>
                        {lote.categoria_codigo && <span className="ml-2 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">{lote.categoria_codigo}</span>}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-primary">{lote.percentagem || 0}%</p>
                      <p className="text-xs text-stone-500">
                        {opsConcluidas} / {opsTotal} operações
                      </p>
                    </div>
                  </div>
                  
                  <div className="w-full h-2 bg-stone-200 rounded-full overflow-hidden mb-3">
                    <div 
                      className={`h-full rounded-full ${(lote.percentagem || 0) >= 100 ? 'bg-green-500' : 'bg-primary'}`}
                      style={{ width: `${Math.min(lote.percentagem || 0, 100)}%` }}
                    />
                  </div>
                  
                  <div className="grid grid-cols-5 gap-2">
                    <div className="text-center p-2 bg-white rounded-lg">
                      <p className="text-lg font-bold text-stone-700">{lote.quantidade}</p>
                      <p className="text-[10px] text-stone-500">Peças</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded-lg">
                      <p className="text-lg font-bold text-green-600">
                        {opsConcluidas}/{opsTotal}
                      </p>
                      <p className="text-[10px] text-stone-500">Op.</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded-lg">
                      <p className="text-lg font-bold text-red-600">{lote.defeitos || 0}</p>
                      <p className="text-[10px] text-stone-500">Defeitos</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded-lg">
                      <p className="text-lg font-bold text-amber-600">{lote.arranjos || 0}</p>
                      <p className="text-[10px] text-stone-500">Arranjos</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded-lg">
                      <p className="text-base font-bold text-blue-600">{formatarTempo(lote.tempo_total_segundos)}</p>
                      <p className="text-[10px] text-stone-500">Tempo</p>
                    </div>
                  </div>
                  
                  {/* Operadores do lote */}
                  {lote.participantes && lote.participantes.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-stone-200">
                      <p className="text-xs font-medium text-stone-600 mb-2 flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" />
                        Operadores neste lote ({lote.participantes.length})
                      </p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {lote.participantes.map((p) => (
                          <div key={p.id} className="bg-white p-2 rounded-lg border flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                              {p.nome?.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{p.nome}</p>
                              <p className="text-[10px] text-stone-500">
                                {p.quantidade} pçs • {p.tempo_horas || 0}h
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {(!lote.participantes || lote.participantes.length === 0) && (
                    <div className="mt-3 pt-3 border-t border-stone-200 text-center text-stone-400 py-2">
                      <Users className="h-5 w-5 mx-auto mb-1 opacity-30" />
                      <p className="text-xs">Nenhum operador trabalhou neste lote</p>
                    </div>
                  )}
                </Card>
              );
            })()}
            
            {/* Operações */}
            <div>
              <h2 className="font-heading font-bold text-lg flex items-center gap-2 mb-4">
                <Layers className="h-5 w-5 text-primary" />
                Operações ({operacoesFiltradas.length})
                {filtroLote !== 'todos' && (
                  <span className="text-sm font-normal text-stone-500">- dados do lote selecionado</span>
                )}
              </h2>
              
              <div className="space-y-3">
                {operacoesFiltradas.map((op, idx) => {
                  const categoria = getCategoriaInfo(op.categoria_id);
                  // Usar dados específicos do lote se um lote está selecionado
                  const qtdProduzida = filtroLote !== 'todos' ? (op.quantidade_produzida_lote || 0) : (op.quantidade_produzida || 0);
                  const percentagem = filtroLote !== 'todos' ? (op.percentagem_lote || 0) : (op.percentagem || 0);
                  const defeitosOp = filtroLote !== 'todos' ? (op.defeitos_lote || 0) : (op.defeitos || 0);
                  const arranjosOp = filtroLote !== 'todos' ? (op.arranjos_lote || 0) : (op.arranjos || 0);
                  const lote = filtroLote !== 'todos' ? acompanharData?.lotes?.find(l => l.lote_id === filtroLote) : null;
                  const qtdTotal = lote ? (lote.quantidade_ajustada || lote.quantidade) : acompanharData?.of?.quantidade_total;
                  // Calcular quantidade disponível (descontando anomalias do tipo defeito)
                  const anomaliasLote = lote ? (lote.total_anomalias || 0) : 0;
                  const qtdDisponivel = lote ? Math.max(0, qtdTotal - anomaliasLote) : qtdTotal;
                  
                  return (
                    <Card key={op.operacao_id || idx} className="p-3 border-2 border-navy/40">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <span className="font-mono text-xs bg-stone-100 px-1.5 py-0.5 rounded">{op.codigo}</span>
                            <span className="font-medium text-sm">
                              {op.nome}
                              {op.is_contagem && (
                                <span className="ml-1.5 text-[10px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded-full font-bold">
                                  CONT
                                </span>
                              )}
                            </span>
                            <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">{categoria.nome}</span>
                            {/* Badges de estado em tempo real */}
                            {op.estado_producao_lote === 'em_curso' && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-bold animate-pulse" style={{ background: '#059669', color: '#FFF' }}>
                                EM CURSO
                              </span>
                            )}
                            {op.estado_producao_lote === 'pausado' && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#FEF3C7', color: '#92400E' }}>
                                PAUSA
                              </span>
                            )}
                            {op.estado_producao_lote === 'congelada' && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5" style={{ background: '#DBEAFE', color: '#1D4ED8' }}>
                                <Snowflake className="h-2.5 w-2.5" />
                                CONGELADA
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-stone-400">Ordem: {(op.ordem || 0) + 1}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-xl font-bold ${qtdProduzida > qtdTotal ? 'text-red-600' : 'text-primary'}`}>
                            {Math.min(percentagem, 100)}%
                            {qtdProduzida > qtdTotal && <span className="text-xs ml-1">!</span>}
                          </p>
                          <p className={`text-[10px] ${qtdProduzida > qtdTotal ? 'text-red-500 font-medium' : 'text-stone-500'}`}>
                            {qtdProduzida} / {qtdTotal} un.
                          </p>
                          {lote && anomaliasLote > 0 && (
                            <p className="text-[10px] text-red-500">Disponível: {qtdDisponivel} un.</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Aviso se produção excede quantidade do lote */}
                      {qtdProduzida > qtdTotal && (
                        <div className="mb-2 p-1.5 bg-red-50 rounded border border-red-200 flex items-center gap-1.5">
                          <AlertCircle className="h-3 w-3 text-red-600 flex-shrink-0" />
                          <p className="text-[10px] text-red-700">
                            Quantidade produzida ({qtdProduzida}) excede o total ({qtdTotal}). Verifique os registos.
                          </p>
                        </div>
                      )}
                      
                      {/* Info do Lote quando selecionado */}
                      {lote && qtdProduzida <= qtdTotal && (
                        <div className="mb-2 p-1.5 bg-stone-50 rounded border border-stone-200">
                          <div className="flex items-center justify-between text-[10px]">
                            <span className="font-medium text-stone-600">Lote {lote.codigo_lote || lote.numero_lote}</span>
                            <div className="flex items-center gap-2">
                              <span>Total: <strong>{qtdTotal}</strong></span>
                              {anomaliasLote > 0 && (
                                <span className="text-red-600">Anomalias: <strong>{anomaliasLote}</strong></span>
                              )}
                              <span className={anomaliasLote > 0 ? 'text-amber-600' : 'text-green-600'}>
                                Disponível: <strong>{qtdDisponivel}</strong>
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="w-full h-1.5 bg-stone-200 rounded-full overflow-hidden mb-2">
                        <div 
                          className={`h-full rounded-full transition-all ${percentagem >= 100 ? 'bg-green-500' : percentagem > 0 ? 'bg-primary' : 'bg-stone-300'}`}
                          style={{ width: `${Math.min(percentagem, 100)}%` }}
                        />
                      </div>
                      
                      {/* Defeitos, Arranjos, Tempo e Operadores - layout compacto */}
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        {/* Lado esquerdo: Defeitos e Arranjos */}
                        <div className="flex items-center gap-2">
                          {defeitosOp > 0 && (
                            <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">
                              <AlertCircle className="h-3 w-3" />
                              {defeitosOp} def.
                            </span>
                          )}
                          {arranjosOp > 0 && (
                            <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                              <Clock className="h-3 w-3" />
                              {arranjosOp} arr.
                            </span>
                          )}
                        </div>
                        
                        {/* Lado direito: Tempo */}
                        {(op.tempo_total_segundos > 0 || op.tempo_segundos_lote > 0) && (
                          <span className="flex items-center gap-1 text-xs text-stone-500">
                            <Clock className="h-3 w-3" />
                            {formatarTempo(op.tempo_total_segundos || op.tempo_segundos_lote || 0)}
                          </span>
                        )}
                      </div>
                      
                      {/* Operadores atribuídos e Produção por operador - layout horizontal */}
                      <div className="flex items-start justify-between gap-4">
                        {/* Operadores atribuídos */}
                        {op.operadores_atribuidos && op.operadores_atribuidos.length > 0 && (
                          <div className="flex-1 min-w-0">
                            <span className="text-xs text-stone-500">Operadores atribuídos:</span>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {op.operadores_atribuidos.map(opId => {
                                const operador = operadores.find(o => o.id === opId);
                                if (!operador) return null;
                                const nomeCompleto = operador.nome_completo || operador.nome || '';
                                const partes = nomeCompleto.trim().split(' ').filter(Boolean);
                                const nomeFormatado = partes.length >= 2 
                                  ? `${partes[0]} ${partes[partes.length - 1]}`
                                  : partes[0] || operador.username;
                                return (
                                  <span 
                                    key={opId}
                                    className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full"
                                  >
                                    {nomeFormatado}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Botão Editar Registos */}
                        {op.participantes && op.participantes.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs text-blue-600 hover:text-blue-700 flex-shrink-0"
                            onClick={() => {
                              setSelectedOperacao(op);
                              setShowRegistosDialog(true);
                            }}
                            data-testid={`editar-registos-btn-${op.operacao_id}`}
                          >
                            <Edit3 className="h-3 w-3 mr-1" />
                            Editar Registos
                          </Button>
                        )}
                        
                        {/* Botão Criar Registo Manual */}
                        {filtroLote !== 'todos' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs text-green-600 hover:text-green-700 flex-shrink-0"
                            onClick={() => {
                              setCriarRegistoOp(op);
                              setCriarRegistoLote(filtroLote);
                              setCriarRegistoModo('registo');
                              setShowCriarRegisto(true);
                            }}
                            data-testid={`criar-registo-btn-${op.operacao_id}`}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Criar Registo
                          </Button>
                        )}
                        
                        {/* Botão Tarefa Concluída — abre modal */}
                        {filtroLote !== 'todos' && percentagem < 100 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs text-emerald-600 hover:text-emerald-700 flex-shrink-0"
                            onClick={() => {
                              setCriarRegistoOp(op);
                              setCriarRegistoLote(filtroLote);
                              setCriarRegistoModo('concluir');
                              setShowCriarRegisto(true);
                            }}
                            data-testid={`concluir-op-btn-${op.operacao_id}`}
                          >
                            <CheckSquare className="h-3 w-3 mr-1" />
                            Concluir
                          </Button>
                        )}
                      </div>
                      
                      {/* Produção por operador - linha única compacta */}
                      {op.participantes && op.participantes.length > 0 && (
                        <div className="mt-2">
                          <span className="text-xs text-stone-500 mr-2">Produção:</span>
                          <div className="inline-flex flex-wrap gap-1.5">
                            {op.participantes.map((p) => (
                              <div key={p.id} className="inline-flex items-center gap-1 bg-stone-50 px-2 py-0.5 rounded-full text-xs">
                                <div className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">
                                  {p.nome?.charAt(0)}
                                </div>
                                <span className="font-medium">{p.nome?.split(' ')[0]}</span>
                                <span className="text-stone-500">({p.quantidade})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
                
                {operacoesFiltradas.length === 0 && (
                  <Card className="p-8 text-center text-stone-500">
                    <Layers className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>Nenhuma operação encontrada para os filtros seleccionados</p>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Análise de Tempos */}
      <AnaliseTemposOF
        open={showAnaliseTempos}
        onOpenChange={setShowAnaliseTempos}
        of={ofCompleta}
        operacoesBase={operacoesBase}
        categorias={categorias}
        quantidade={ofCompleta?.quantidade}
      />

      {/* Dialog Lista de Registos para Edição */}
      <Dialog open={showRegistosDialog} onOpenChange={(open) => {
        setShowRegistosDialog(open);
        if (!open) {
          setSelectedOperacao(null);
          setRegistosOperacao([]);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="registos-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5 text-primary" />
              Registos de Produção - {selectedOperacao?.nome || 'Operação'}
            </DialogTitle>
            <DialogDescription>
              Clique num registo para editar os dados (hora, quantidade, etc.)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 mt-4">
            {/* Resumo com anomalias */}
            {selectedOperacao && (
              <div className="flex flex-wrap gap-4 px-3 py-2 bg-stone-50 rounded-lg border border-stone-200 text-xs">
                <span>Total produzido: <b className="text-primary">{registosOperacao.reduce((s, r) => s + (r.quantidade_produzida || 0), 0)}</b></span>
                {(selectedOperacao.defeitos || selectedOperacao.defeitos_lote || 0) > 0 && (
                  <span className="text-red-600">Defeitos: <b>{selectedOperacao.defeitos || selectedOperacao.defeitos_lote || 0}</b></span>
                )}
                {(selectedOperacao.arranjos || selectedOperacao.arranjos_lote || 0) > 0 && (
                  <span className="text-amber-600">Arranjos: <b>{selectedOperacao.arranjos || selectedOperacao.arranjos_lote || 0}</b></span>
                )}
                {((selectedOperacao.defeitos || selectedOperacao.defeitos_lote || 0) + (selectedOperacao.arranjos || selectedOperacao.arranjos_lote || 0)) > 0 && (
                  <span className="text-red-700 font-bold">Anomalias: {(selectedOperacao.defeitos || selectedOperacao.defeitos_lote || 0) + (selectedOperacao.arranjos || selectedOperacao.arranjos_lote || 0)}</span>
                )}
                <span>Registos: <b>{registosOperacao.length}</b></span>
              </div>
            )}
            {registosOperacao.length === 0 ? (
              <div className="text-center py-8 text-stone-500">
                <Clock className="h-12 w-12 mx-auto mb-3 text-stone-300" />
                <p>Nenhum registo de produção encontrado para esta operação.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-stone-600 border-b bg-stone-50">
                      <th className="py-2 px-3">Operador</th>
                      <th className="py-2 px-3">Lote</th>
                      <th className="py-2 px-3">Data</th>
                      <th className="py-2 px-3">Início</th>
                      <th className="py-2 px-3">Fim</th>
                      <th className="py-2 px-3 text-right">Duração</th>
                      <th className="py-2 px-3 text-right">Peças</th>
                      <th className="py-2 px-3 text-right">Eficiência</th>
                      <th className="py-2 px-3 text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registosOperacao.map((reg) => {
                      const inicio = reg.inicio ? new Date(reg.inicio) : null;
                      const fim = reg.fim ? new Date(reg.fim) : null;
                      const duracao = reg.tempo_produtivo_segundos || reg.tempo_total_segundos || 0;
                      const horas = Math.floor(duracao / 3600);
                      const mins = Math.floor((duracao % 3600) / 60);
                      const duracaoStr = horas > 0 ? `${horas}h ${mins}m` : `${mins}m`;
                      const foiEditado = reg.historico_edicoes && reg.historico_edicoes.length > 0;
                      
                      return (
                        <tr key={reg.id} className={`border-b last:border-0 hover:bg-stone-50 ${foiEditado ? 'bg-amber-50/50' : ''}`}>
                          <td className="py-2.5 px-3">
                            <div className="flex items-center gap-2">
                              {reg.operador_foto ? (
                                <img src={reg.operador_foto} alt="" className="w-6 h-6 rounded-full object-cover" />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                                  {reg.operador_nome?.charAt(0) || '?'}
                                </div>
                              )}
                              <span className="font-medium">{reg.operador_nome || 'N/A'}</span>
                              {foiEditado && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium" title={`Editado ${reg.historico_edicoes.length}x - ${reg.motivo_edicao || 'Correção'}`}>
                                  EDITADO
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-2.5 px-3">
                            <span className="font-mono text-xs bg-stone-100 px-1.5 py-0.5 rounded">{reg.lote_codigo || reg.lote_id?.substring(0, 6) || '-'}</span>
                          </td>
                          <td className="py-2.5 px-3 text-stone-600">
                            {formatOnlyDateLisbon(reg.inicio)}
                          </td>
                          <td className="py-2.5 px-3 font-mono text-xs">
                            {formatTimeLisbon(reg.inicio)}
                          </td>
                          <td className="py-2.5 px-3 font-mono text-xs">
                            {reg.fim ? formatTimeLisbon(reg.fim) : (reg.ativo ? 'Em curso' : '-')}
                          </td>
                          <td className="py-2.5 px-3 text-right">{duracaoStr}</td>
                          <td className="py-2.5 px-3 text-right font-semibold">{reg.quantidade_produzida || 0}</td>
                          <td className={`py-2.5 px-3 text-right font-bold ${
                            (reg.eficiencia_percentual || 0) >= 100 ? 'text-green-600' :
                            (reg.eficiencia_percentual || 0) >= 80 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {Math.round(reg.eficiencia_percentual || 0)}%
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => {
                                setSelectedRegistoId(reg.id);
                                setShowEditarRegisto(true);
                              }}
                              title="Editar registo"
                              data-testid={`editar-registo-${reg.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setShowRegistosDialog(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Edição de Registo Individual */}
      <EditarRegisto
        open={showEditarRegisto}
        onOpenChange={setShowEditarRegisto}
        registoId={selectedRegistoId}
        onSuccess={() => {
          // Recarregar registos e dados
          if (selectedOperacao) {
            loadRegistosOperacao(selectedOperacao.operacao_id);
          }
          loadData();
        }}
      />

      {/* Modal unificado: Criar Registo / Concluir Operação */}
      <CriarRegistoManual
        open={showCriarRegisto}
        onOpenChange={setShowCriarRegisto}
        ofId={ofId}
        loteId={criarRegistoLote}
        operacaoId={criarRegistoOp?.operacao_id}
        operadores={operadores}
        modo={criarRegistoModo}
        gestorNome="Gestor"
        quantidadeDisponivel={(() => {
          if (!criarRegistoLote || criarRegistoLote === 'todos') return null;
          const lote = acompanharData?.lotes?.find(l => l.id === criarRegistoLote);
          if (!lote) return null;
          const qtdTotal = lote.quantidade_ajustada || lote.quantidade || 0;
          const anomalias = lote.total_anomalias || 0;
          return Math.max(0, qtdTotal - anomalias);
        })()}
        operacaoNome={criarRegistoOp ? `${criarRegistoOp.operacao_codigo || ''} ${criarRegistoOp.operacao_nome || ''}`.trim() : ''}
        onSuccess={() => {
          loadData();
        }}
      />
    </div>
  );
}
